import type { Messages } from './fa'

const en: Messages = {
  common: {
    loading: 'Loading…',
    back: 'Back',
    copy: 'Copy',
    copied: 'Copied',
    error: 'Error',
    retry: 'Retry',
    play: 'Play',
    go: 'Go',
    save: 'Save',
    send: 'Send',
    cancel: 'Cancel',
    confirm: 'Confirm',
    friend: 'friend',
    noName: 'no name',
    all: 'Total',
    daily: 'Daily',
    monthly: 'Monthly',
    rank: 'Rank',
    league: 'League',
    bigMatch: 'Big match',
    user: 'User',
    submitting: 'Submitting…',
    sending: 'Sending…',
    watchingAd: 'Showing ad…',
    free: 'Free',
    search: 'Search',
    cardMasked: 'Card: **** **** **** {last4}',
  },
  coins: {
    gold: 'Gold',
    silver: 'Silver',
    bronze: 'Bronze',
    diamond: 'Diamond',
    coin: 'Coin',
  },
  nav: {
    aria: 'Main menu',
    home: 'Home',
    games: 'Games',
    football: 'Football',
    horse: 'Horse',
    airdrop: 'Airdrop',
    gift: 'Gift',
    profile: 'Profile',
  },
  home: {
    hello: 'Hi, {name}',
    luckyTitle: 'Lucky Numbers',
    luckySub: '4 stages · bronze to diamond · 20 min',
    quickStart: 'Quick start',
    allGames: 'All games ‹',
    tiles: {
      airdrop: { title: 'Daily airdrop', subtitle: 'Daily rewards' },
      guess: { title: 'Guess number', subtitle: '7 digits · no 4' },
      football: { title: 'Football', subtitle: 'League & big match' },
      horse: { title: 'Horse poll', subtitle: 'Races & predictions' },
      gift: { title: 'Gift card', subtitle: 'Get a gift code' },
      wallet: { title: 'Wallet', subtitle: 'Coin balances' },
      cash: { title: 'Cash out', subtitle: 'Withdraw prizes' },
      profile: { title: 'Profile', subtitle: 'Level & invites' },
      support: { title: 'Support', subtitle: 'Chat with the team' },
    },
    social: {
      sectionAria: 'Our channels',
      title: 'Our channels',
      instagram: 'Instagram',
      telegram: 'Telegram',
      twitter: 'Twitter',
      youtube: 'YouTube',
      tiktok: 'TikTok',
    },
  },
  games: {
    title: 'Games',
    subtitle: 'Pick one and jump in',
    status: 'Status',
    chances: 'Chances',
    of10: '{n} of 10',
    todayNumber: "Today's number",
    revealed: 'Number revealed',
    active: 'Game active',
    waiting: 'Waiting for number',
    cards: {
      guess: {
        title: 'Guess number',
        subtitle: '7-digit number without 4',
      },
      lucky: {
        title: 'Lucky Numbers',
        subtitle: '4 stages · bronze to diamond · 20 min',
        blurb: 'Pick numbers. Out of chances? Watch an ad.',
      },
      morse: {
        title: 'Morse code',
        subtitle: 'Morse key · gold coin',
        blurb: 'Build the word with Morse. Correct: gold; referrer: silver.',
      },
      horse: {
        title: 'Horse poll',
        subtitle: 'League · 3-in-4 · twins',
        blurb: 'Predict race results with bronze coins for gold/diamond prizes.',
      },
    },
  },
  wallet: {
    title: 'Wallet',
    subtitle: 'Balances and cash out',
    approxValue: 'Euro value',
    cashTitle: 'Cash out coins',
    cashHint: 'To cash out with admin or transfer to another user, go to Cash out.',
    goCashOut: 'Go to cash out',
    convertTitle: 'Convert coins',
    convertHint: 'Admin sets each coin’s euro price. You only receive whole target coins; the rest stays in the source coin.',
    convertFrom: 'From',
    convertTo: 'To',
    convertAmount: 'Amount',
    convertBalance: 'Balance: {n}',
    convertValue: 'Value of {amount} {coin}: {eur}',
    convertResult: 'You get {receive} {to} · {leftover} {from} stays',
    convertTooSmall: 'At least {min} {from} for one {to}',
    convertSame: 'Source and target must be different',
    convertSubmit: 'Convert',
    convertOk: '{spent} {from} became {receive} {to}',
    convertNeedFunds: 'Not enough coins',
    eachRate: 'Each coin {eur}',
  },
  cashOut: {
    title: 'Cash out',
    subtitle: 'Transfer coins or withdraw via admin',
    unlock: 'Unlock with PIN',
    setPin: 'Set PIN',
    enterPin: 'Enter PIN',
    pinHint: '4–8 digit PIN',
    toAdmin: 'To admin',
    toUser: 'To user',
    amount: 'Amount',
    coinType: 'Coin type',
    walletAddress: 'Wallet address',
    recipient: 'Recipient ID',
    submit: 'Submit request',
    transfer: 'Transfer',
    history: 'History',
    statusCompleted: 'Completed',
    statusPending: 'Pending',
    statusApproved: 'Admin approved',
    statusRejected: 'Rejected',
    pinSetOk: 'PIN set successfully',
    pinUnlockOk: 'Cash out unlocked',
    pinWrong: 'Wrong PIN',
    pinSetErr: 'Could not set PIN',
    transferOk: 'Transfer successful',
    transferErr: 'Transfer failed',
    adminOk: 'Cash-out request submitted',
    adminErr: 'Could not submit request',
    errPinLength: 'PIN must be 4 to 6 digits',
    errUnlockFirst: 'Unlock cash out with your PIN first',
    errAmount: 'Enter the coin amount',
    errInsufficient: 'Insufficient balance',
    errRecipient: 'Enter the recipient ID or username',
    errWallet: 'Enter the wallet address',
    intro: 'Cash out the coins you earned from games and prizes, or send them to another user.',
    sectionTitle: 'Enter cash out',
    pinSetupHint: 'Set a PIN for this section first',
    unlockedHint: 'Cash out is unlocked',
    pinEnterHint: 'Enter your PIN to continue',
    unlockPinBtn: 'Unlock with PIN',
    lock: 'Lock',
    lockedBanner: 'Cash out is locked. Set or enter your PIN first.',
    tabAdmin: 'Transfer to admin',
    tabUser: 'Transfer to user',
    walletPlaceholder: 'Destination wallet address',
    recipientLabel: 'Recipient ID or username',
    recipientPlaceholder: 'e.g. 84729103 or @username',
    amountLabel: 'Coin amount',
    amountPlaceholder: 'Amount',
    submitAdmin: 'Submit cash out to admin',
    submitUser: 'Transfer to user',
    historyTitle: 'Cash-out history',
    historyEmpty: 'No requests yet',
    historyToUser: 'To user {id}',
    historyToAdmin: 'To admin',
    modalSetTitle: 'Set cash-out PIN',
    modalUnlockTitle: 'Enter cash out',
    modalSetBody: 'Choose a 4–6 digit PIN so only you can open this section.',
    modalUnlockBody: 'Enter your cash-out PIN',
    pinPlaceholder: '4–6 digit PIN',
    modalSetSubmit: 'Set and enter',
    modalUnlockSubmit: 'Enter',
  },
  profile: {
    language: 'Language',
    languageHint: 'Choose the app language',
    invites: 'Invites',
    level: 'Level',
    bestCoin: 'Best coin',
    idLabel: 'ID',
    idCopyTitle: 'Copy ID for coin transfer',
    achievements: 'Achievements',
    badgeFirst: 'First coin',
    badgeInvite: '5 successful invites',
    badgeLucky: 'Lucky stage 3',
    luckyProgress: 'Progress · Lucky Numbers',
    footballScore: 'Football score',
    footballScoreHint: 'Cumulative score is never reset.',
    noLeagueYet: 'You have not joined a league or big match yet',
    footballPrizes: 'Football prizes',
    inviteFriends: 'Invite friends',
    inviteHint: 'Share your code or QR — earn a bronze coin per signup.',
    qrLabel: 'Invite link QR code',
    qrHint: 'Scan to open the invite link',
    copyInviteLink: 'Copy invite link',
    linkCopied: 'Link copied',
    topUsers: 'Top users',
    topUsersHint: 'Leaders in football, horse, airdrop and invites',
    viewTopUsers: 'View top users',
  },
  register: {
    subtitle: 'Enter an invite code to start',
    pendingTeam: 'After signup you will open the invited dream team',
    noCodeTitle: 'No referral code?',
    noCodeHint: 'If you don’t have a referral code, enter @sisil_org in the field below.',
    referralLabel: 'Referral code',
    referralPlaceholder: '@sisil_org',
    getRandomCode: 'Get a random referral code',
    submit: 'Sign up & enter',
    submitting: 'Signing up…',
    foot: 'Use a friend’s code, or enter @sisil_org if you don’t have one.',
    needTelegram: 'Please open the app from the Telegram bot',
    noTgData: 'Telegram data missing. Please enter via the bot.',
    registerFail: 'Signup failed. Please try again.',
    registerErr: 'Signup error.',
    codeFetchErr: 'Could not fetch referral code',
  },
  support: {
    title: 'Support',
    meta: 'Direct chat · messages are deleted after 7 days',
    empty: 'Hi! Write your question here.',
    placeholder: 'Type your message…',
    sendErr: 'Failed to send',
  },
  airdrop: {
    title: 'Airdrop',
    subtitle: 'Daily presence and rewards',
    wallet: 'Wallet',
    leadersToday: "Today's leaders",
    leadersMonth: 'Monthly leaders',
    usefulTime: 'Useful time',
    play: 'Play',
    cooldown: 'Cooldown',
    watchAd: 'Watch ad',
    claim: 'Claim reward',
    toastUsefulCharged: '5 minutes of useful time added',
    scoreToday: "Today's score",
    waitNextTurn: 'Wait for the next turn',
    usefulTimerActive: '5 useful minutes (only while you stay on this page)',
    closedHours: 'Closed from 23:00 to 00:00',
    berlinTime: 'Germany time',
    tapAriaActive: 'Tap to score',
    tapAriaLocked: 'Airdrop inactive',
    glassClosed: 'Closed',
    glassCooldown: 'Turn over',
    glassClosedDetail: '23:00–00:00 · Germany time',
    glassCooldownDetail: 'Wait {time} or watch an ad',
    watchAdInstant: 'Watch an ad · instant 5-minute recharge',
    hintRegistering: 'Submitting…',
    hintConnecting: 'Connecting…',
    hintTimerRunning: 'Timer running · each tap +1',
    hintTimerPaused: 'Timer paused · come back to this page',
    hintNightlyClosed: 'This hour is closed for everyone every night',
    hintCooldownOrAd: 'Wait 5 minutes, or recharge instantly with an ad',
    leadersTablistAria: 'Leaders',
    emptyNoScores: 'No scores yet',
    emptyNoData: 'No data found',
  },
  football: {
    title: 'Football',
    subtitle: 'Choose daily league or big match',
    modeLeague: 'Daily league',
    modeBig: 'Big match',
    dailyLeague: 'Daily league',
    hintLeague:
      'Daily matches: pick win / draw / loss for every game (1–3 picks) and submit together — 1 correct pick=8, 2 picks with a hit=4, all three=2. Daily #1 gets gold (referrer silver); month-end top 5 get gold.',
    hintBig:
      'Weekly matches: pick win / draw / loss and build a dream team; 5 correct predictors are raffled and the best dream team wins a coin.',
    tabMatches: 'Matches',
    tabLeaderboard: 'Leaderboard',
    tabDreamTeams: 'Dream teams',
    tabWinnings: 'My prizes',
    totalScore: 'Total score',
    dayScore: 'Day',
    monthScore: 'Month',
    noScore: 'No scores yet',
    noWinnings: 'No prizes won yet',
    goldCoin: 'Gold coin',
    silverCoin: 'Silver coin',
    submitPredictions: 'Watch video & submit',
    submitting: 'Watching video / submitting…',
    pickRequired: 'Pick at least one outcome per match',
    loadingMatches: 'Loading matches…',
    noBigMatch: 'No big match is set right now',
    noLeagueMatch: 'No league match is set right now',
    noActiveLeague: 'No active league',
    readyBatch: 'Watch a video to submit {n} matches',
    draftHint: 'Join the poll for {n} open matches, then submit together',
    topOf: 'Top of {mode}',
    wonPrizes: 'Prizes won',
    dayMonth: 'Day {day} · Month {month}',
    outcomeHome: 'Win',
    outcomeAway: 'Loss',
    outcomeDraw: 'Draw',
    dayGoalsTitle: 'Guess today’s goal totals',
    dayGoalsHint:
      'Guess total home and away goals across {n} matches today. Both correct → {silver} silver (no referrer bonus). Cost: 1 bronze or one video.',
    dayGoalsHome: 'Total home goals',
    dayGoalsAway: 'Total away goals',
    dayGoalsPayBronze: 'Submit for {n} bronze',
    dayGoalsPayAd: 'Watch video',
    dayGoalsSubmitAfterAd: 'Submit guess',
    dayGoalsAdReadyHint: 'Video watched — now submit your guess',
    dayGoalsSubmitted: 'Guess saved: home {home} · away {away}',
    dayGoalsInvalid: 'Goals must be whole numbers ≥ 0',
    dayGoalsErr: 'Could not submit goal guess',
    draws: {
      daily_correct: 'Daily league raffle',
      weekly_correct: 'Weekly big-match raffle',
      daily_top: 'Daily top scorer',
      daily_referral: 'Daily winner referral bonus',
      monthly_top: 'Monthly top players',
      best_dream_team: 'Best dream team',
      weekly_dream_team_raffle: 'Dream team raffle',
      league_day_goals: 'League day goal totals',
    },
    errSubmitPrediction: 'Could not submit prediction',
    errSubmitPoll: 'Could not submit poll',
    statusLive: 'Live',
    statusFinished: 'Finished',
    statusUpcoming: 'Upcoming',
    yourPoll: 'Your poll',
    yourPrediction: 'Your prediction',
    pickedSuffix: '(relative to {team})',
    pickHint: 'Result from {team}’s view — win / draw / loss:',
    pollClosed: 'The poll for this match is closed',
    predictionClosed: 'Predictions for this match are closed',
    buildDreamTeam: 'Build dream team',
  },
  horse: {
    title: 'Horse poll',
    subtitle: 'Race predictions and prizes',
    modesTitle: 'Modes',
    leaderboard: 'Leaderboard',
    myBets: 'My predictions',
    mode: {
      league: {
        label: 'League',
        fee: 'Free',
        hint: '5–15 races in a row · 1 horse · points 9…1 · winner gold · monthly top',
      },
      three_in_four: {
        label: 'Three in four',
        fee: '2 bronze or 5 videos',
        hint: '4 courses · 4 active each · set 1st…4th order per course · sum score · gold',
      },
      twins: {
        label: 'Twins',
        fee: '2 bronze or 5 videos',
        hint: '5 races · 2 horses · 3 correct gold / 5 correct diamond',
      },
      three_one: {
        label: 'Three-to-one',
        fee: '2 bronze or 5 videos',
        hint: '5 races · 3 horses · 5 first places → gold',
      },
      more_chance: {
        label: 'More chance',
        fee: '1 bronze or 3 videos',
        hint: '5 races · 2 horses · best placing · ≥43 gold',
      },
    },
    errLoad: 'Could not load',
    errGeneric: 'Error',
    errWatchAdsFirst: 'Watch {n} videos first',
    msgSubmitted: 'Poll submitted',
    errNotSubmitted: 'Not submitted',
    msgAdProgress: 'Video counted · {watched} of {required}',
    errAdFailed: 'Ad was not counted',
    finishPosition: 'Place {n}',
    entryPrefix: ' · entry: ',
    monthlyTop: 'Monthly top users',
    yourScore: 'Your score:',
    todayScore: '(today {n})',
    emptyNoScores: 'No scores yet',
    openPollsTitle: 'Open polls',
    emptyNoPolls: 'No race poll',
    roundEntryPaid: 'Entry {bronze} bronze or {ads} videos',
    courseCount: ' · {n} races',
    join: 'Join',
    entrySubmitted: 'Submitted · score: {score}',
    entryWinner: ' · won {amount} {type}',
    orderHint: 'Set the order (1 = further ahead)',
    placeAmongHint: 'Set the order of these 4 active horses: 1st, 2nd, 3rd, 4th',
    placeOption: 'Place {n}',
    pickNHorses: ' · pick {n} horses',
    submitPoll: 'Submit poll',
    submitWithBronze: 'Submit with {n} bronze',
    watchVideos: 'Watch videos ({watched} of {required})',
    submitWithVideos: 'Submit with videos ({n})',
    roundLeaderboard: 'Leaderboard for this round',
    votingClosed: 'Voting is closed — the race has started or an admin closed it',
    historyTitle: 'My poll history',
    historyEmpty: 'You have not joined a poll yet',
    historyView: 'View',
    historyYourPicks: 'Your picks',
    historyOfficial: 'Official result',
    historyPending: 'Result not in yet',
    historyStatusOpen: 'In progress',
    historyStatusWaiting: 'Waiting for the result',
    historyStatusSettled: 'Result in',
    historyScore: 'Score {score}',
    historyCorrect: '{n} correct',
    historyMine: 'Your pick',
    historyPredicted: 'Predicted place {n}',
    historyNoRound: 'This round is no longer available',
    historyOpenPoll: 'Open this poll',
  },
  gift: {
    title: 'Gift card',
    subtitle: 'Get and redeem gift codes',
    redeem: 'Redeem code',
    myCodes: 'My codes',
    statusUsedWithPrize: 'Used · prize: {prize}',
    statusUsed: 'Used',
    statusExpired: 'Expired',
    statusActiveHint: 'Active — tap it to fill in',
    badgeUsed: 'Used',
    badgeExpired: 'Expired',
    badgeActive: 'Active',
    errEnterCode: 'Enter the code',
    errInvalidCode: 'Invalid code',
    noticeAlreadyUsed: 'This code has already been used.',
    noticeExpired: 'This code has expired and is no longer valid.',
    noticeNotFound: 'This code was not found.',
    errPickPrize: 'Pick a prize',
    claimOk: 'Prize saved',
    errClaim: 'Could not save the prize',
    intro:
      'If you won the airdrop, enter the one-hour code here to see the prizes and pick one.',
    codeLabel: 'Reward code',
    codePlaceholder: 'e.g. AIRDWARD-...',
    enterPrizes: 'See prizes',
    activeCodesTitle: 'Active codes ({n})',
    inactiveCodesTitle: 'Used / expired codes ({n})',
    activeCodeLabel: 'Active code',
    pickTitle: 'Pick one prize',
    claimPrize: 'Claim prize',
    doneTitle: 'Prize saved',
    doneNote: 'This code has been used and can no longer be redeemed.',
    exit: 'Leave gifts',
  },
  guess: {
    title: 'Guess number',
    subtitle: '7 digits without 4',
    errSevenDigits: 'The number must be 7 digits',
    errNoFour:
      'The digit 4 is not allowed — enter a number without 4 (allowed digits: 0, 1, 2, 3, 5, 6, 7, 8, 9)',
    errSubmit: 'Could not submit guess',
    statusUnavailable: 'Could not load game status',
    revealAt: 'Revealed at 23:30',
    todayPrompt: "What's today's number?",
    hint: 'Guess a 7-digit number with no digit 4. You can submit each number only once.',
    placeholder: 'e.g. 1235678',
    attemptsLeft: 'Chances left',
    submit: 'Submit guess',
    revealedTitle: "Today's number is revealed",
    bannerWin: 'Congrats! You won today',
    bannerLose: "You didn't win today",
    winnersCount: '{n} winners today',
    noWinners: 'No winners today',
    waitingNoNumber: "Today's number isn't set yet",
    waitingSchedule: 'Generated at 00:01 · revealed at 23:30',
    untilReveal: 'Until reveal: {countdown}',
    winnersToday: "Today's winners",
    userHash: 'User #{id}',
    correctTag: 'Correct',
  },
  lucky: {
    title: 'Lucky Numbers',
    subtitle: '4 stages to diamond',
    unlimited: 'Unlimited',
    toastDemotedStageChance: 'Time or chances for this stage ran out — back to stage 1',
    toastPickExact: 'Pick exactly {n} numbers',
    toastDemotedStage: 'Time for this stage ran out — back to stage 1',
    toastAdCharged: '✅ Chances recharged',
    chancesLabel: 'Chances',
    stepsAria: 'Stages',
    windowClosed: "Today's play window is closed. Come back tomorrow.",
    allDoneTitle: 'All stages done for today!',
    allDoneHint: 'You get fresh chances tomorrow.',
    sessionExpiredStage1:
      "This session timed out. Today's 3 free chances stay as they are and refill tomorrow. Videos are still unlimited.",
    sessionExpiredRestart: 'Time for this stage ran out. Restarting sends you back to stage 1.',
    restart: 'Restart',
    hintPick: 'Pick {pick} out of {pool} numbers',
    hintOneWinner:
      'Only 1 number out of {pool} is correct — if it is among your picks, you win',
    rewardLine: 'Reward: {coin} coin · picked {selected}/{pick}',
    stage1Quota: '3 free chances for the whole day; they refill tomorrow · each video +5 chances',
    stageFreePrefix: 'Free this stage: {free}',
    noAds: ' · no ads',
    adsRemaining: ' · ads left: {n}',
    stageTimeLimit: ' · 20 minutes',
    confirmPicks: 'Confirm picks',
    needAdNoFree: 'This stage has no free chances. Watch an ad to guess.',
    needAdRecharge: 'Out of chances. Watch an ad to recharge.',
    watchAdPlus: 'Watch an ad · +{n} chances',
    loseDemotedTitle: 'Back to stage 1',
    loseTitle: 'You lost!',
    correctWas: 'The correct number was',
    loseDemotedHint: "You're back at stage 1. Today's free chances are whatever is left.",
    loseNumbersChanged: 'The lucky numbers changed for your next try.',
    attemptsLeft: 'Chances left: {n}',
    continueStage1: 'Continue at stage 1',
    tryAgain: 'Try again',
  },
  morse: {
    title: 'Morse code',
    subtitle: 'Build the word in Morse',
    errEnterWord: 'Enter the word with the Morse key',
    windowClosedTitle: "Today's play window is closed",
    windowClosedHint: 'It reopens tomorrow from midnight until 23:30 Germany time',
    solved: 'Solved',
    timeLeft: 'Time left',
    clock: 'Clock',
    keyTitle: 'Morse key',
    keyHint: 'Short = dot · hold = dash. After a pause, the letter is saved.',
    readoutWord: 'Word',
    keyLabel: 'Morse',
    keyHoldHint: 'hold · · —',
    backspace: '⌫ Delete',
    submitGuess: 'Submit guess',
    clearAll: 'Clear all',
    sessionEnded: "This round's session has ended",
  },
  topUsers: {
    title: 'Top users',
    subtitle: 'Leaders across sections',
    emptyFootball: 'No football league scores yet',
    emptyHorse: 'No horse-racing league scores yet',
    emptyAirdrop: 'No airdrop scores yet',
    emptyInvite: 'No invites yet',
    scoreLabel: 'Score',
    inviteLabel: 'Invites',
  },
  dreamTeam: {
    title: 'Dream team',
    titlePlural: 'Dream teams',
    brandBoard: 'Dream team · Sisil',
    defaultTeamName: 'My dream team',
    matchVs: '{home} vs {away}',
    listSeparator: ', ',
    positions: {
      gk: 'Goalkeeper',
      def: 'Defender',
      mid: 'Midfielder',
      fwd: 'Forward',
      voteShort: {
        def: 'Defense',
        fwd: 'Attack',
      },
    },
    status: {
      draft: 'Draft',
      submitted: 'Submitted',
      submittedSpaced: 'Submitted',
      notSubmitted: 'Not submitted',
      teamSubmittedCheck: 'Team submitted ✓',
      teamSubmittedHeading: '✅ Dream team submitted',
    },
    explore: {
      ariaSearch: 'Search',
      searchPlaceholder: 'Search…',
      empty: 'Empty',
      notFound: 'Not found',
      exploreTeams: '🔍 Explore teams',
    },
    view: {
      notFound: 'This dream team was not found',
      backToExplore: 'Back to explore',
      labelTeamName: 'Dream team name',
      labelFormation: 'Formation',
      metaRating: '★ {avg} ({votes}) · {totalScore} pts',
      creator: 'Creator: {name}',
      buildMyTeam: 'Build my team',
      pitchAlt: 'Dream team pitch animation',
    },
    builderPage: {
      matchNotFound: 'Match not found',
      backToFootball: 'Back to football',
    },
    builder: {
      toastEnterName: 'Enter a dream team name',
      toastPlayersRemaining: '{remaining} players still open ({selected} of {total})',
      toastSaveError: 'Could not save dream team',
      toastUpdated: 'Dream team updated',
      toastCreated: 'Dream team created',
      toastSubmitted: 'Dream team submitted',
      toastSubmitError: 'Could not submit dream team',
      toastAnimationFailed: 'Could not build the animation',
      toastLinkCopied: 'Team link copied',
      toastFileNotReady: 'File is not ready yet',
      toastWaitForFile: 'Wait until the file is ready…',
      toastVideoSaved: 'Video saved — use that same video in your story so the animation plays',
      toastGifSaved: 'GIF saved — if the story stays still, use the video',
      toastStoryStepsVideo:
        'Video is in your gallery → new story → pick that video → paste the link',
      toastStoryStepsFile:
        'File is in your gallery → new story → pick that file → paste the link',
      labelTeamName: 'Dream team name',
      labelFormation: 'Formation',
      placeholderTeamName: 'e.g. My dream team',
      activeCardGroups: 'Active card groups for this match:',
      noActiveCards:
        'No cards are active for this match. An admin must pick this match’s card groups in the Football tab.',
      pickEmpty: 'Pick',
      playersSelected: 'Players selected: {selected} of {total}',
      filterAll: 'All ({count})',
      pickPositionTitle: '{icon} Pick {position}',
      pickSlotHint: 'Pick a spot on the pitch',
      cardCount: '{count} cards',
      tapEmptySlot: 'Tap an empty spot on the pitch',
      noCardsForPosition: 'No card for this position in this match’s active groups',
      searchPlayerPlaceholder: 'Search player name...',
      badgeSelected: 'Selected',
      badgeInTeam: 'In team',
      badgeAdd: '+ Add',
      submittedBlurb:
        'After you submit, the lineup locks and enters the raffle and the Best Dream Team contest.',
      copyLink: 'Copy link',
      shareTeam: '📤 Share team',
      submitTeam: 'Submit team',
      saving: 'Saving...',
      updateTeam: 'Update team',
      buildTeam: 'Build team ({selected}/{total})',
      shareModal: {
        title: 'Story with pitch animation',
        buildingComposition: 'Building the lineup animation…',
        fileNotReady: 'File is not ready yet',
        instagramTip:
          'Instagram usually freezes gallery GIFs — post a short video so the cards move.',
        instagramSteps:
          '1) Save/share this file 2) Story → that same video/GIF 3) Paste the link with a sticker.',
        building: 'Building animation…',
        shareVideo: '📤 Save / share pitch video',
        shareGif: '📤 Save / share pitch GIF',
        downloadVideo: 'Download video',
        downloadGif: 'Download GIF',
        copyBotLink: 'Copy bot link',
      },
    },
    image: {
      stickerHint: 'Paste the link on your story with a link sticker',
      cta: 'Build your own dream team and earn points',
      unknownInitial: '?',
    },
    voting: {
      sectionTitle: 'Rate (1 to 5)',
      alreadyVoted: 'You already voted',
      yourRating: 'Your rating: {rating}/5',
      pollClosed: 'The poll for this match is closed',
      pollClosedHint: 'Voting is only open before the match starts',
      ownerHint: 'This is your team; others can vote on it.',
      slotLine: '{position} · slot {slot}',
      toastPollClosed: 'The poll for this match is closed',
      toastVoteRecorded: 'Your vote was saved',
      toastVoteError: 'Could not save your vote',
      toastCannotVoteOwn: "You can't vote on your own team",
      votingOpen: 'Voting open',
      votingClosed: 'Closed',
      yourTeamChip: 'Your team',
      topTeamsTitle: 'Best teams in this match',
    },
  },
  fantasy: {
    explore: {
      subtitleRefreshing: 'Updating…',
      subtitleDefault: 'Search and vote',
      searchPlaceholder: 'Search team name or link',
      resultsFor: 'Results for “{query}”',
      loadError: 'Could not load teams',
      emptySearch: 'No team found with that name or link',
      emptyDefault: 'No active teams to vote on yet',
      backToMatches: 'Back to matches',
    },
    builder: {
      title: 'Dream team builder',
      stepOf: 'Step {step} of 2',
      stepTeamName: '📝 Team name',
      stepFormation: '🧩 Formation',
      stepCardGroups: '📁 Card groups for this match',
      cardGroupsHint: 'On the next step you can only pick cards from these groups.',
      nextStep: 'Next: place the players →',
      registeredLineup: '📋 Saved lineup',
      toastEnterName: 'Enter a team name',
      toastPickAllSlots: 'Pick a player for each of the {totalSlots} slots',
      toastLineupUpdated: 'Lineup updated',
      toastTeamCreated: 'Team created',
      toastSaveError: 'Could not save team',
      toastSubmittedForPoll: 'Team submitted for the poll',
      toastSubmitError: 'Could not submit team',
      toastLinkCopied: 'Team link copied',
      toastCopyFailed: 'Could not copy the link',
      shareVoteText: 'Vote for my dream team: {teamName}',
      charCount: '{length}/40',
      pickSlotHint: 'Pick a slot on the pitch',
      noCardsForPosition: 'No card for this position in this match’s groups',
      shareAndPoll: 'Share team and open the poll',
      copyTeamLink: 'Copy team link',
      seeOtherTeams: 'See other teams',
      submitForPoll: 'Submit for poll',
      saveTeam: 'Save team ({selected}/{total})',
      saveHint: 'Pick all {totalSlots} players to enable Save',
    },
    vote: {
      notFound: 'Team not found, or the link is invalid',
      back: 'Back',
    },
  },
}

export default en
